import { memo, useCallback, useMemo, useState } from 'react';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';

import { useGlobalContext } from 'context/GlobalContext';
import { StatusCode } from 'helpers/app.config';
import GeneralModalChangeStatus, {
  type FormFieldsChangeStatus
} from 'components/actions/GeneralModalChangeStatus';
import { CustomBasicOptionsModal } from 'components/shared';
import { AvailableStatus, TicketUpdateResponse } from 'api/ticket';
import useInvalidateQueries from 'layouts/Main/components/Notifications/hooks/useInvalidateQueries';
import useUpdateTicketStatus from 'components/actions/ChangeStatus/hooks/useUpdateTicketStatus';

import useRealTimeNotifications from './hooks/useRealTimeNotifications';
import useNotificationsFromHttp from './hooks/useNotificationsFromHttp';
import Notification from './components/Notification';
import SearchUnreadNotificationsInput from './components/SearchUnreadNotificationsInput';
import MarkAllReadButton from './components/MarkAllReadButton';
import LoadMoreButton from './components/LoadMoreButton';
import NoPendingNotifications from './components/NoPendingNotifications';
import GeneralLoading from './components/GeneralLoading';
import BottomLoading from './components/BottomLoading';
import NoMatchMessage from './components/NoMatchMessage';
import useApiStatusChange from './hooks/useApiStatusChange';
import { DestinyStatus } from './hooks/useNotificationActions';

/**
 * Notifications component displays real-time notifications for the user.
 * It handles fetching, displaying, and managing notifications, including
 * marking them as read and loading more notifications.
 *
 * @component
 * @returns {JSX.Element} The rendered Notifications component.
 *
 * @example
 * Usage <Notifications />
 *
 * @hooks
 * - useGlobalContext: Retrieves user data and context.
 * - useStatusChangeWrapperModal: Manages modal state for status changes.
 * - useTranslation: Provides translation functionality.
 * - useMsal: Manages Microsoft authentication.
 * - useRealTimeNotifications: Handles real-time notifications fetching and state.
 * - useNotificationsFromHttp: Manages notifications fetched from HTTP.
 * - useApiStatusChange: Provides API functions for updating ticket statuses.
 *
 * @state
 * - ticketId: The ID of the ticket being processed.
 * - notificationId: The ID of the current notification.
 * - destinyStatus: The status object for managing notification actions.
 * - isLogoutModal: State to control the logout confirmation modal.
 * - anchorEl: The element that the popover is anchored to.
 *
 * @methods
 * - handleClick: Opens the notifications popover.
 * - handleClose: Closes the notifications popover and resets search.
 * - handleReadNotification: Marks a specific notification as read.
 * - handleReadAll: Marks all notifications as read.
 * - handleLoadMore: Loads more notifications based on the current search.
 * - handleReject: Handles rejection of a ticket status change.
 * - acceptLogout: Logs the user out.
 * - cancelLogout: Cancels the logout action.
 * - handleOpenLogoutModal: Opens the logout confirmation modal.
 */
function Notifications(): JSX.Element {
  const { userData } = useGlobalContext();
  const receiverUserId = userData.extension_GSerUserId;
  const { invalidateQueries } = useInvalidateQueries();
  const { t } = useTranslation('global');
  const { instance } = useMsal();

  const [ticketId, setTicketId] = useState<string>(null);
  const [notificationId, setNotificationId] = useState<number>(null);
  const [destinyStatus, setDestinyStatus] = useState<DestinyStatus>({
    code: null,
    message: null,
    title: null,
    acceptButtonText: null,
    commentTitle: null
  });
  const [isLogoutModal, setIsLogoutModal] = useState(false);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const {
    actualRealTimePageNumber,
    handleLoadMoreRealTimeNotifications,
    handleReadAllRealTimeNotifications,
    handleReadRealTimeNotification,
    isRealTimeNotificationsFetching,
    isRealTimeNotificationsLoading,
    realTimeNotifications,
    realTimeTotalCount,
    refetchRealTimeNotifications,
    setRealTimeNotifications,
    setRealTimeNotificationsParams,
    setRealTimeTotalCount
  } = useRealTimeNotifications({ receiverUserId, open });

  const {
    handleLoadMoreNotificationsFromHttp,
    handleReadAllNotificationsFromHttp,
    handleReadNotificationFromHttp,
    isNotificationsFromHttpFetching,
    isNotificationsFromHttpLoading,
    notificationsFromHttp,
    searchForm,
    searchValue,
    setDebouncedSearchTerm
  } = useNotificationsFromHttp({
    realTimeNotifications,
    realTimeTotalCount,
    receiverUserId,
    refetchRealTimeNotifications,
    setRealTimeNotifications,
    setRealTimeNotificationsParams,
    setRealTimeTotalCount
  });

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    []
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setDebouncedSearchTerm('');
    searchForm.setValue('search', '');
  }, [setDebouncedSearchTerm, searchForm]);

  const { updateTicketToScheduled } = useApiStatusChange();
  const updateTicketStatus = useUpdateTicketStatus();

  const notifications = useMemo(() => {
    if (searchValue === '') return realTimeNotifications;

    return notificationsFromHttp;
  }, [notificationsFromHttp, realTimeNotifications, searchValue]);

  const handleCloseGeneralModalChangeStatus = useCallback(() => {
    setDestinyStatus(null);
  }, []);

  const displayNotifications = useMemo(
    () =>
      (actualRealTimePageNumber !== 1 && isRealTimeNotificationsLoading) ||
      (!isRealTimeNotificationsLoading && realTimeTotalCount !== 0),
    [
      actualRealTimePageNumber,
      isRealTimeNotificationsLoading,
      realTimeTotalCount
    ]
  );

  const displayLoadMoreButton = useMemo(
    () =>
      !isRealTimeNotificationsLoading &&
      realTimeTotalCount >= 200 &&
      notifications.length !== 0 &&
      realTimeTotalCount > notifications.length,
    [isRealTimeNotificationsLoading, realTimeTotalCount, notifications.length]
  );

  const displayMarkAllReadButton = useMemo(
    () =>
      ((actualRealTimePageNumber !== 1 && isRealTimeNotificationsLoading) ||
        !isRealTimeNotificationsLoading) &&
      notifications.length !== 0,
    [
      actualRealTimePageNumber,
      isRealTimeNotificationsLoading,
      notifications.length
    ]
  );

  const displayBottomLoading = useMemo(
    () =>
      searchValue === '' &&
      isRealTimeNotificationsFetching &&
      notifications.length !== 0,
    [isRealTimeNotificationsFetching, notifications.length, searchValue]
  );

  const displayGeneralLoading = useMemo(() => {
    if (isNotificationsFromHttpLoading || isNotificationsFromHttpFetching)
      return true;
    if (notifications.length === 0 && searchValue !== '') return false;

    return notifications.length === 0 && realTimeTotalCount !== 0;
  }, [
    isNotificationsFromHttpFetching,
    isNotificationsFromHttpLoading,
    notifications.length,
    realTimeTotalCount,
    searchValue
  ]);

  const displayNoPendingNotifications = useMemo(
    () => !isRealTimeNotificationsLoading && realTimeTotalCount === 0,
    [isRealTimeNotificationsLoading, realTimeTotalCount]
  );

  const displayNoMatchMessage = useMemo(
    () =>
      !isNotificationsFromHttpFetching &&
      !isNotificationsFromHttpLoading &&
      notifications.length === 0 &&
      searchValue !== '',
    [
      isNotificationsFromHttpLoading,
      notifications.length,
      searchValue,
      isNotificationsFromHttpFetching
    ]
  );

  const handleReadNotification = useCallback(
    (notificationID: number) => {
      if (searchValue !== '') {
        handleReadNotificationFromHttp(notificationID);
      } else {
        handleReadRealTimeNotification(notificationID);
      }
    },
    [
      handleReadNotificationFromHttp,
      handleReadRealTimeNotification,
      searchValue
    ]
  );

  const handleReadAll = useCallback(() => {
    if (searchValue !== '') {
      handleReadAllNotificationsFromHttp();
    } else {
      handleReadAllRealTimeNotifications();
    }
  }, [
    handleReadAllNotificationsFromHttp,
    handleReadAllRealTimeNotifications,
    searchValue
  ]);

  const navigate = useNavigate();

  const handleShowAllNotifications = () => {
    navigate('/notifications', {});
  };

  const handleLoadMore = useCallback(() => {
    if (searchValue !== '') {
      handleLoadMoreNotificationsFromHttp();
    } else {
      handleLoadMoreRealTimeNotifications();
    }
  }, [
    handleLoadMoreNotificationsFromHttp,
    handleLoadMoreRealTimeNotifications,
    searchValue
  ]);

  const handleReject = (form: UseFormReturn<FormFieldsChangeStatus>) => {
    const { comment } = form.getValues();

    switch (destinyStatus?.code) {
      case StatusCode.Scheduled:
        updateTicketToScheduled.mutate(
          {
            ticketId: +ticketId,
            comment,
            scheduledApproved: false
          },
          {
            onSuccess: async ({
              data
            }: {
              data: { data: TicketUpdateResponse[] };
            }) => {
              form.reset();
              handleReadNotification(notificationId);
              await invalidateQueries(Number(data?.data[0]?.ticketId));
              setTicketId(null);
              setNotificationId(null);
              setDestinyStatus({
                code: null,
                message: null,
                title: null,
                acceptButtonText: null,
                commentTitle: null
              });
            },
            onError: () => {
              setTicketId(null);
              setNotificationId(null);
              setDestinyStatus({
                code: null,
                message: null,
                title: null,
                acceptButtonText: null,
                commentTitle: null
              });

              form.reset();
            }
          }
        );
        break;
      case StatusCode.Working:
        updateTicketStatus.mutate(
          {
            ticketId: +ticketId,
            finalStatusId: destinyStatus?.code,
            comment: `${destinyStatus?.message}
            ${comment}`
          },
          {
            onSuccess: async ({
              data
            }: {
              data: { data: TicketUpdateResponse[] };
            }) => {
              form.reset();
              handleReadNotification(notificationId);
              await invalidateQueries(+ticketId);
              setTicketId(null);
              setNotificationId(null);
              setDestinyStatus({
                code: null,
                message: null,
                title: null,
                acceptButtonText: null,
                commentTitle: null
              });
            },
            onError: () => {
              setTicketId(null);
              setNotificationId(null);
              setDestinyStatus({
                code: null,
                message: null,
                title: null,
                acceptButtonText: null,
                commentTitle: null
              });

              form.reset();
            }
          }
        );
        break;
      default:
        break;
    }
  };

  const acceptLogout = async () => instance.logout();
  const cancelLogout = () => setIsLogoutModal(false);
  const handleOpenLogoutModal = () => setIsLogoutModal(true);

  return (
    <>
      <IconButton
        color="inherit"
        disabled={isRealTimeNotificationsLoading}
        onClick={handleClick}
      >
        <Badge badgeContent={realTimeTotalCount} color="secondary">
          <NotificationsIcon style={{ color: '#0E335C' }} />
        </Badge>
      </IconButton>

      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        onClose={handleClose}
        open={open}
      >
        <Paper
          elevation={6}
          sx={{
            padding: '16px',
            minWidth: '288px',
            width: {
              xs: '100%',
              sm: '500px'
            },
            borderRadius: '8px'
          }}
        >
          <MarkAllReadButton
            disabled={
              isRealTimeNotificationsLoading ||
              (!displayGeneralLoading && !displayMarkAllReadButton)
            }
            handleReadAll={handleReadAll}
            handleShowAllNotifications={handleShowAllNotifications}
            realTimeTotalCount={realTimeTotalCount}
          />

          {displayNoPendingNotifications && <NoPendingNotifications />}

          {realTimeTotalCount !== 0 && (
            <SearchUnreadNotificationsInput
              form={searchForm}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
            />
          )}

          {displayNoMatchMessage && <NoMatchMessage />}

          {displayGeneralLoading && <GeneralLoading />}

          {!displayGeneralLoading &&
            displayNotifications &&
            notifications.map((notification) => (
              <Notification
                actions={notification.actions}
                advise={notification.advise}
                emmitedDate={notification.emmitedDate}
                entityId={notification.entityId}
                entityType={notification.entityType}
                expireDate={notification.expireDate}
                handleClose={handleClose}
                handleReadNotification={handleReadNotification}
                iconUrl={notification.iconUrl}
                key={notification.notificationId}
                message={notification.message}
                needsAttention={notification.needsAttention}
                notificationId={notification.notificationId}
                notificationKey={notification.notificationKey}
                objects={notification.objects}
                ruleId={notification.ruleId}
                setDestinyStatus={setDestinyStatus}
                setNotificationId={setNotificationId}
                setOpenLogoutModal={handleOpenLogoutModal}
                setTicketId={setTicketId}
                ticketIdActive={notification.ticketId}
                title={notification.title}
                viewed={notification.viewed}
              />
            ))}

          {displayBottomLoading && <BottomLoading />}

          {!displayGeneralLoading && displayLoadMoreButton && (
            <LoadMoreButton
              disabled={isRealTimeNotificationsFetching}
              handleLoadMoreRealTimeNotifications={handleLoadMore}
            />
          )}
        </Paper>
      </Popover>
      <GeneralModalChangeStatus
        acceptButtonText={destinyStatus?.acceptButtonText}
        commentTitle={destinyStatus?.commentTitle}
        destinyStatus={
          {
            id: destinyStatus?.code,
            name: destinyStatus?.title
          } as AvailableStatus
        }
        isMutating={
          updateTicketToScheduled?.isLoading || updateTicketStatus?.isLoading
        }
        isOpen={!!destinyStatus?.code}
        notificationMaxLength={4000}
        onCancel={handleCloseGeneralModalChangeStatus}
        onSubmit={handleReject}
        ticketId={ticketId}
        title={destinyStatus?.title}
        isObligatory
        isTicketStatusToClosed
      />
      {isLogoutModal && (
        <CustomBasicOptionsModal
          acceptSubmit={acceptLogout}
          btnAcceptText={t('common.commonActions.accept')}
          btnCancelText={t('common.commonActions.cancel')}
          cancelSubmit={cancelLogout}
          message={t('common.commonMessages.userRoleChanged')}
          show={isLogoutModal}
        />
      )}
    </>
  );
}

export default memo(Notifications);
